/**
 * HR-portal FTUE — Internal Jobs page steps (`/internal-jobs`).
 *
 * Owns the four on-page coachmarks of the `portal-hr-activation` tour that walk
 * an HR admin through the Internal Jobs page, each its own single-concept phase
 * (no "N of M" chrome), matching the resume-builder journey:
 *  - `internalJobsAdd` — the "Add opening" button
 *  - `internalJobsImport` — the "Import CSV" button
 *  - `internalJobsFilters` — the status segmented control + search row
 *  - `internalJobsRowActions` — the results table's "Actions" column header
 *
 * The steps are registered on the shared `portal-hr-activation` flow at orders
 * `internalJobsAdd`-`internalJobsRowActions` (between `nav-internal-jobs` and
 * `wrap-up`), so they slot into the one linear tour that the layout hook
 * (`use-portal-hr-ftue`) starts.
 *
 * They live in their own page hook — rather than the layout registry — so each
 * `condition` can close over live page state (whether the openings list has
 * finished its initial load, and whether it has any rows), matching
 * `use-opportunities-ftue` / `use-learn-ftue` / `use-portal-import-ftue`.
 *
 * The "Add opening" and "Import CSV" buttons are always mounted and each opens a
 * dialog with no navigation, so both are `dismissOnAction`: a click on either
 * reads as intent and retires that step. The filters row wraps two different
 * controls (segmented control + search form), so it stays a plain "Got it" ack
 * rather than reacting to either one. The row-actions header is likewise a plain
 * "Got it" ack — anchoring the static `<th>` rather than the per-row, `is_open`
 * -gated "View matches" button, which would be a fragile anchor.
 */

import { getStep, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useEffect, useRef } from 'react'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_STEP,
  onPortalPath,
  portalStep,
  reevaluatePortalHrFlow,
} from './flow'

export interface PortalInternalJobsFtueArgs {
  /** Whether create/import/edit controls are available to this stakeholder. */
  canManage?: boolean
  /**
   * The openings list has finished its initial fetch (`!isLoading` from
   * `useInternalJobs`). Gates all four steps so none fire against a
   * still-loading page; the anchored elements are always mounted, so this only
   * defers timing. Defaults to `true` when the hook is called with no args.
   */
  ready?: boolean
  /**
   * The openings list has at least one row (`jobs.length > 0`). Gates
   * `internalJobsRowActions` only: the "Actions" column header is not a
   * useful coachmark target with an empty table, so that step is skipped
   * silently until there is a row to act on. Defaults to `true` when the hook
   * is called with no args.
   */
  hasJobs?: boolean
}

const INTERNAL_JOBS_PATH = '/internal-jobs'

/** This hook's own step ids, read by the re-eval effect below so it only ever
 *  retires a step this hook registered, never one owned by another hook on the
 *  shared flow. */
const OWN_STEP_IDS: readonly string[] = [
  PORTAL_HR_STEP.internalJobsAdd,
  PORTAL_HR_STEP.internalJobsImport,
  PORTAL_HR_STEP.internalJobsFilters,
  PORTAL_HR_STEP.internalJobsRowActions,
]

/**
 * Registers the four Internal Jobs on-page steps and returns the anchor refs
 * for the "Add opening" button, the "Import CSV" button, the filters row, and
 * the results table's "Actions" header. Call once from `PortalInternalJobsPage`.
 */
export function usePortalInternalJobsFtue({
  ready = true,
  hasJobs = true,
  canManage = true,
}: PortalInternalJobsFtueArgs = {}) {
  const addOpeningAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.jobsAddCta)
  const importCtaAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.jobsImportCta)
  const filtersAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.jobsFilters)
  const rowActionsAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.jobsRowActions)

  // The steps are registered once below, so their `condition` closures must read
  // the current flags rather than the mount-time snapshot.
  const readyRef = useRef(ready)
  readyRef.current = ready
  const hasJobsRef = useRef(hasJobs)
  hasJobsRef.current = hasJobs
  const canManageRef = useRef(canManage)
  canManageRef.current = canManage

  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.internalJobsAdd,
        phase: PORTAL_HR_STEP.internalJobsAdd,
        order: PORTAL_HR_ORDER.internalJobsAdd,
        trigger: { type: 'immediate' },
        condition: () =>
          onPortalPath(INTERNAL_JOBS_PATH) && readyRef.current && canManageRef.current,
        anchorId: PORTAL_HR_ANCHOR.jobsAddCta,
        placement: 'bottom',
        // "Add opening" is one small button that opens a dialog (no nav), so a
        // click on it reads as intent — retire the step rather than leave the
        // coachmark hanging over the open dialog.
        dismissOnAction: true,
        content: {
          body: 'Post an internal opening here, or import a CSV. Pelgo matches each role to employees by department and skills.',
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.internalJobsImport,
        phase: PORTAL_HR_STEP.internalJobsImport,
        order: PORTAL_HR_ORDER.internalJobsImport,
        trigger: { type: 'immediate' },
        condition: () =>
          onPortalPath(INTERNAL_JOBS_PATH) && readyRef.current && canManageRef.current,
        anchorId: PORTAL_HR_ANCHOR.jobsImportCta,
        placement: 'bottom',
        // Same reasoning as `internalJobsAdd` above: one small button that opens
        // a dialog (no nav), so a click on it reads as intent.
        dismissOnAction: true,
        content: {
          body: 'Bulk-add openings from a CSV instead of one at a time.',
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.internalJobsFilters,
        phase: PORTAL_HR_STEP.internalJobsFilters,
        order: PORTAL_HR_ORDER.internalJobsFilters,
        trigger: { type: 'immediate' },
        condition: () => onPortalPath(INTERNAL_JOBS_PATH) && readyRef.current,
        anchorId: PORTAL_HR_ANCHOR.jobsFilters,
        placement: 'bottom',
        // The anchored row wraps two different controls (segmented control +
        // search form), so no single click reads as "done" — plain "Got it" ack.
        content: {
          body: 'Filter by Open, Closed, or All, or search by title, department, or location.',
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.internalJobsRowActions,
        phase: PORTAL_HR_STEP.internalJobsRowActions,
        order: PORTAL_HR_ORDER.internalJobsRowActions,
        trigger: { type: 'immediate' },
        // Only a candidate once the table has a row — the "Actions" header is
        // not a useful target against an empty state.
        condition: () => onPortalPath(INTERNAL_JOBS_PATH) && readyRef.current && hasJobsRef.current,
        anchorId: PORTAL_HR_ANCHOR.jobsRowActions,
        placement: 'bottom',
        // Anchors the static header rather than the per-row, `is_open`-gated
        // "View matches" button, which would be a fragile anchor. Plain "Got it"
        // ack — the header itself does nothing on click.
        content: {
          body: 'Click View matches on any open role to see ranked internal candidates, or edit and close a listing from here.',
          dismissLabel: 'Got it',
        },
      }),
    ])

    // Registration alone does not trigger evaluation — start the shared flow so
    // the engine re-picks the next eligible step. Idempotent; the layout hook
    // owns the actual start rule.
    reevaluatePortalHrFlow()
  }, [])

  // Re-evaluate when readiness or the presence of rows changes (a plain
  // re-render does not re-run the engine). If the active step is one of this
  // hook's own four and its `condition` has since flipped false while still
  // unseen, mark it seen so the tour advances cleanly; otherwise just re-pick
  // the candidate. Scoped to this hook's own step ids so it never retires a
  // step owned by another hook on the shared flow. Mirrors `use-learn-ftue` /
  // `use-portal-import-ftue`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ready/hasJobs intentionally re-trigger evaluation; live state is read via getState()
  useEffect(() => {
    const { activeStepId, seen, complete } = useFtueStore.getState()
    if (activeStepId && OWN_STEP_IDS.includes(activeStepId) && !seen.has(activeStepId)) {
      const step = getStep(PORTAL_HR_ACTIVATION_FLOW, activeStepId)
      if (step?.condition && !step.condition()) {
        complete(activeStepId)
        return
      }
    }
    reevaluatePortalHrFlow()
  }, [ready, hasJobs, canManage])

  return {
    /** Attach to the "Add opening" `<Button>` in the Internal Jobs header. */
    addOpeningAnchor,
    /** Attach to the "Import CSV" `<Button>` in the Internal Jobs header. */
    importCtaAnchor,
    /** Spread onto the outer row wrapping the status segmented control and the
     *  search `<form>`. */
    filtersAnchor,
    /** Attach to the results table's static `<th>Actions</th>` header. */
    rowActionsAnchor,
  }
}
